'use client';

/**
 * TripPlannerPanel — the planner's shell.
 *
 *   ┌ header ─── ONE component on every screen: a 56px band with a photo  ┐
 *   │            of where you're headed (a brand gradient until one loads),│
 *   │            scrims under the name in white (tap it to rename; the     │
 *   │            pencil shows on hover), the save chip at the right end    │
 *   │            ("Saved · just now", settling to a quiet "Saved")         │
 *   │            13h 7m · 1,019 km · $360 [fuel v]                         │
 *   │            3 stops · 2 days · 1 night · caravan pace                 │
 *   │            1 day over your 6h limit · Fix        (only when true)    │
 *   │            [Add dates v | Caravan v | 6h a day v]                    │
 *   ├ top ────── "add a stop" box + Ask AI (never scrolls away)            ┤
 *   │ body ───── the ONE scroll container (day headings pin to its top)    │
 *   ├ footer ─── toast · Save · Share · ⋯  on a solid white bar            ┤
 *   │            (fixed to the screen's bottom edge on phones)             │
 *   └──────────────────────────────────────────────────────────────────────┘
 *
 * Phones (`sheet`, inside PlannerBottomSheet): ONE fixed header at every
 * snap (name, figures, "Dates and vehicle", the add-a-stop box), the list
 * scrolling under it, the action bar at the sheet's foot. At peek the body
 * steps aside for a preview of the stops.
 *
 * `fill` makes the panel a flex column that fits its parent: the body
 * scrolls, header/top/toast/footer stay put. Without `fill` the panel is
 * content-height (collapsed or empty).
 *
 * Deliberately no overflow-hidden on the card: the place autocomplete in the
 * top slot drops its listbox over the body.
 */
import React from 'react';
import { IconChevronDown, IconChevronUp, IconInfo, IconPencil, IconSliders } from './icons';
import { formatDrive, formatKm } from './geo';
import type { TripStop } from './types';
import { TripStatsStrip } from './TripSummaryHeader';
import type { TripStat } from './tripStats';

export interface TripPlannerPanelProps {
  /** Current trip name ('' = not named yet; the placeholder shows instead). */
  title: string;
  /** Shown when the name is empty, e.g. "Melbourne to Sydney". */
  titlePlaceholder: string;
  /** Present once there is a trip to name. Without it the title is static text. */
  onTitleChange?: (value: string) => void;
  /** Enter in the name field (re-saves a saved trip under the new name). */
  onTitleCommit?: () => void;
  /** "4 stops · ≈ 1,285 km · 18h 21m · 2 nights". Each part stays on one line. */
  summary: string;
  /**
   * Set when the figures are straight-line estimates: the "≈" part gets an
   * info icon with this explanation. The only place "≈" is explained.
   */
  estimateNote?: string;
  /**
   * Save status, shown as muted meta text UNDER the trip name ("Saved on
   * this device · Back up"). Never a pill beside it: the title row stays the
   * name alone.
   */
  badge?: React.ReactNode | ((compact: boolean | 'dot') => React.ReactNode);
  /**
   * @deprecated The trip's one overflow menu lives in the footer (next to
   * Export). Still rendered if passed, for callers outside the explorer.
   */
  headerActions?: React.ReactNode;
  /** One flat surface (no card border, radius or shadow) — the desktop rail. */
  flat?: boolean;
  /** @deprecated The route thumbnail is gone; the title sits flush left. */
  thumbnailStops?: TripStop[];
  /**
   * Candidate cover photos, best first (the destination, then the other
   * stops). ONE becomes the trip's hero, with the name set over it.
   */
  coverPhotos?: { url: string; name: string }[];
  /**
   * The trip's own pictures, in stop order: start, a night on the way, the
   * destination. Two or more: the hero is a collage of THIS route, not a
   * stock shot of where it ends.
   */
  collagePhotos?: { url: string; name: string }[];
  /**
   * The trip at a glance (stops · nights · km · drive · fuel). Present → it
   * replaces the one-line summary, and the dates/vehicle row always shows
   * (the fuel figure follows the vehicle, so the vehicle stays in reach).
   */
  stats?: TripStat[];
  /** A quiet line under the figures. */
  statsNote?: React.ReactNode;
  /** The road route is still being worked out: the strip says so. */
  statsPending?: boolean;
  /** Right end of the strip's second line (Plan my nights). */
  statsAside?: React.ReactNode;
  /** What the figures' times assume, when it isn't a route planned for the rig. */
  statsBasis?: string;
  /** Tooltip for statsBasis. */
  statsBasisNote?: string;
  /** The plan's biggest problem, said in the header with its fix. */
  statsWarning?: { text: string; title?: string; onFix?: () => void };
  /** Beside the trip name: switch to another saved trip. Gets whether it sits on the hero photo. */
  titleMenu?: React.ReactNode | ((onImage: boolean) => React.ReactNode);
  /** Date range + vehicle row under the title. */
  meta?: React.ReactNode;
  /**
   * Phones: the dates/vehicle/pace row folds into this one summary line
   * ("+ Add dates · Caravan · 6h a day ⌄"); a tap opens the three controls.
   */
  metaSummary?: React.ReactNode;
  /** A message at the top of the plan, under the header ("Your copy of …" after Take this trip). */
  notice?: React.ReactNode;
  open: boolean;
  onToggle: () => void;
  fill?: boolean;
  top?: React.ReactNode;
  footer?: React.ReactNode;
  /** The status toast. Gets its own row above the footer, never over the list. */
  toast?: React.ReactNode;
  /**
   * Phones (bottom sheet): header, add-stop box and list scroll together as
   * one, so the list isn't squeezed under a tall fixed header. The footer
   * still stays put. Also drops the card chrome (the sheet is the card), the
   * collapse chevron (the grab handle does that) and folds the meta row into
   * the summary line.
   */
  sheet?: boolean;
  /**
   * No header at all: the body leads with its own heading (the empty
   * planner's "Plan a road trip"), so a title and summary above it would
   * say the same thing twice.
   */
  hideHeader?: boolean;
  /**
   * Phones, sheet at its lowest snap: the body steps aside for one summary
   * row (name · figures) above the footer, so the actions stay in reach and
   * the map gets the screen. The body stays mounted (scroll position and any
   * half-typed stop survive). Ignored without a header to summarise.
   */
  peek?: boolean;
  /** Tapping the peek summary (lifts the sheet). */
  onExpand?: () => void;
  /**
   * Phones, drop-a-pin armed: the sheet's header becomes the instruction
   * ("Tap the map to add a stop · Goes between …"), docked to the top of
   * the sheet instead of a card floating over the map.
   */
  pinPrompt?: React.ReactNode;
  /**
   * Phones: one pill floating centred just above the footer (the full
   * sheet's "Map"), over the list's bottom fade, never in a row.
   */
  floatingAction?: React.ReactNode;
  /**
   * Phones, peek with a trip: the stops in order ("A Melbourne › B Lakes
   * Entrance › C Sydney") under the figures, so the lowest snap still
   * previews the trip.
   */
  peekPreview?: React.ReactNode;
  className?: string;
  children?: React.ReactNode;
}

export default function TripPlannerPanel({
  title,
  titlePlaceholder,
  onTitleChange,
  onTitleCommit,
  summary,
  estimateNote,
  stats,
  statsNote,
  statsPending = false,
  statsAside,
  statsBasis,
  statsBasisNote,
  statsWarning,
  titleMenu,
  badge,
  headerActions,
  flat = false,
  meta,
  metaSummary,
  notice,
  coverPhotos,
  collagePhotos,
  open,
  onToggle,
  fill = false,
  top,
  footer,
  toast,
  sheet = false,
  hideHeader = false,
  peek = false,
  onExpand,
  pinPrompt,
  floatingAction,
  peekPreview,
  className = '',
  children,
}: TripPlannerPanelProps) {
  const bodyId = 'trip-planner-body';
  // Collapsed, the header is the only way back in: it hides only while open.
  const headerless = hideHeader && open;
  const scrollTogether = sheet && fill && open;
  const peeking = scrollTogether && peek && !headerless;
  const headerRef = React.useRef<HTMLElement>(null);
  // Phones: the sheet's header never scrolls. The list scrolls under it; a
  // soft fade joins it once rows pass under it, and the bottom edge fades
  // while there is more below.
  const bodyRef = React.useRef<HTMLDivElement>(null);
  const [edges, setEdges] = React.useState({ scrolled: false, more: false });
  const measureEdges = React.useCallback(() => {
    const el = bodyRef.current;
    if (!el) return;
    const scrolled = el.scrollTop > 2;
    const more = el.scrollHeight - el.clientHeight - el.scrollTop > 12;
    setEdges((p) => (p.scrolled === scrolled && p.more === more ? p : { scrolled, more }));
  }, []);
  React.useEffect(() => {
    if (!scrollTogether || peeking) return;
    const el = bodyRef.current;
    if (!el) return;
    measureEdges();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measureEdges);
    ro.observe(el);
    Array.from(el.children).forEach((c) => ro.observe(c));
    return () => ro.disconnect();
  }, [scrollTogether, peeking, measureEdges]);
  // Dates and vehicle: on the phone sheet they tuck behind one "Dates and
  // vehicle" button in the header (the controls open under the figures), so
  // the half sheet spends its height on stops. In a sheet without `fill`
  // the dates/vehicle row folds into one summary line instead.
  // With the figures strip the three controls always show (one segmented
  // row, the same on every screen); only the old summary-line header folds.
  const metaInSheetHeader = scrollTogether && !!meta;
  const metaFolds = sheet && !!meta && !stats && !metaInSheetHeader;
  const [metaOpen, setMetaOpen] = React.useState(false);
  const showMeta = open && !!meta && !metaInSheetHeader && (!metaFolds || metaOpen);
  const metaId = 'trip-planner-meta';

  const [failedCovers, setFailedCovers] = React.useState<Set<string>>(() => new Set());
  const markFailed = (url: string) => setFailedCovers((prev) => new Set(prev).add(url));
  const canHero = open && !!onTitleChange;
  const hero = canHero ? (coverPhotos || []).find((p) => !failedCovers.has(p.url)) ?? null : null;
  // Distinct places only: two shots of one town is not a collage.
  const collage = canHero
    ? (collagePhotos || [])
        .filter((p) => !failedCovers.has(p.url))
        .filter((p, i, all) => all.findIndex((q) => q.name === p.name) === i)
        .slice(0, 3)
    : [];
  // One photo, not a collage: where you're headed, else the first stop that
  // has a picture. A single image takes a scrim cleanly; three seams don't.
  const heroPhoto = hero ?? collage[collage.length - 1] ?? null;
  // The same header on every screen (the desktop rail and the phone sheet):
  // the band always shows once there is a trip to name (a brand gradient
  // stands in until a photo loads). The floating card keeps its plain title
  // row and its collapse chevron.
  const showHero = canHero && (sheet || flat);
  // The collapse chevron sits in the title row, never floating on a photo
  // (an unlabelled circle there read as "scroll up?").
  const collapseButton = !sheet && !showHero && (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      aria-controls={open ? bodyId : undefined}
      aria-label={open ? 'Collapse the planner' : 'Expand the planner'}
      title={open ? 'Collapse the planner' : 'Expand the planner'}
      className="flex h-10 w-10 min-h-0 flex-none items-center justify-center rounded-tp border-0 bg-transparent text-tp-faint transition-colors duration-150 ease-tp-out -my-1 -mr-2 hover:bg-tp-surface hover:text-tp-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-tp-brand/45"
    >
      {open ? <IconChevronUp className="h-5 w-5" /> : <IconChevronDown className="h-5 w-5" />}
    </button>
  );
  const badgeFor = (compact: boolean | 'dot') => (typeof badge === 'function' ? badge(compact) : badge);
  // The phone's condensed title bar keeps what the header said: the save
  // state (as a dot), the distance, the time and the fuel guess.
  const compactFigures = (() => {
    const by = (k: TripStat['key']) => stats?.find((s) => s.key === k);
    const km = by('km');
    const drive = by('drive');
    const fuel = by('fuel');
    if (!stats || statsPending || !(km || drive)) return summary.split(' · ').slice(1, 3).join(' · ');
    return [
      km ? `${km.value} ${km.unit}` : null,
      drive ? `${drive.value}${drive.unit}${drive.extra ? ` ${drive.extra.value}${drive.extra.unit}` : ''}` : null,
      fuel ? fuel.value : null,
    ]
      .filter(Boolean)
      .join(' · ');
  })();
  const statusLine = badge ? (
    <div className="mt-1.5 flex min-h-[24px] items-center animate-tp-fade motion-reduce:animate-none" data-testid="trip-badge">
      {badgeFor(false)}
    </div>
  ) : null;
  const titleRow = (onImage: boolean) => (
    <div className="flex min-w-0 items-center gap-1">
      <TitleInput title={title} placeholder={titlePlaceholder} onChange={onTitleChange!} onCommit={onTitleCommit} onImage={onImage} />
      {typeof titleMenu === 'function' ? titleMenu(onImage) : titleMenu}
    </div>
  );
  const header = (
    <header ref={headerRef} className={`flex-none px-4 pb-3 ${sheet ? 'pt-1' : 'pt-3.5'}`}>
      {showHero ? (
        <TripHero
          photo={heroPhoto}
          onError={markFailed}
          corner={
            badge ? (
              <div className="animate-tp-fade motion-reduce:animate-none" data-testid="trip-badge">
                {badgeFor(false)}
              </div>
            ) : null
          }
        >
          {titleRow(true)}
        </TripHero>
      ) : sheet && onTitleChange ? (
        // Phones: one line. Name and pencil on the left, the save state on
        // the right; no thumbnail to push the stops down.
        <div className="flex min-h-[36px] min-w-0 items-center gap-2">
          <div className="min-w-0 flex-1">{titleRow(false)}</div>
          {badge ? (
            <div className="flex flex-none items-center" data-testid="trip-badge">
              {badgeFor(true)}
            </div>
          ) : null}
        </div>
      ) : (
        <>
          <div className="flex min-w-0 items-center gap-1">
            {onTitleChange ? (
              <>
                <TitleInput title={title} placeholder={titlePlaceholder} onChange={onTitleChange} onCommit={onTitleCommit} />
                {typeof titleMenu === 'function' ? titleMenu(false) : titleMenu}
              </>
            ) : (
              <h2 className="m-0 min-w-0 flex-1 text-tp-xl font-semibold text-tp-ink" style={{ fontSize: 20 }}>
                {title || titlePlaceholder}
              </h2>
            )}
            {headerActions && <div className="-my-1 flex flex-none items-center">{headerActions}</div>}
            {collapseButton}
          </div>
          {statusLine}
        </>
      )}
      {stats && stats.length ? (
        <div className={showHero ? 'mt-3' : sheet ? 'mt-1.5' : 'mt-2.5'}>
          <TripStatsStrip stats={stats} pending={statsPending} basis={statsBasis} basisNote={statsBasisNote} warning={statsWarning} />
          {statsNote}
        </div>
      ) : null}
      {stats && stats.length && metaFolds && metaSummary && open ? (
        // Phones: when, what you drive and how far a day, as one line. One
        // tap opens the three controls.
        <button
          type="button"
          onClick={() => setMetaOpen((v) => !v)}
          aria-expanded={metaOpen}
          aria-controls={metaId}
          className="mt-2.5 flex h-10 min-h-0 w-full items-center gap-2 rounded-tp border border-solid border-tp-line bg-white px-3 text-left text-[13px] font-medium text-tp-ink transition-colors duration-150 ease-tp-out hover:border-tp-line-strong hover:bg-tp-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tp-brand/45 [font-family:inherit]"
          data-testid="trip-meta-summary"
        >
          <span className="min-w-0 flex-1 truncate">{metaSummary}</span>
          <span className="sr-only">{metaOpen ? 'Hide dates, vehicle and pace' : 'Change dates, vehicle and pace'}</span>
          <IconChevronDown
            className={`h-4 w-4 flex-none text-tp-muted transition-transform duration-220 ease-tp-out ${metaOpen ? 'rotate-180' : ''}`}
          />
        </button>
      ) : stats && stats.length ? null : metaFolds && open ? (
        <button
          type="button"
          onClick={() => setMetaOpen((v) => !v)}
          aria-expanded={metaOpen}
          aria-controls={metaId}
          title={metaOpen ? 'Hide dates and vehicle' : 'Dates and vehicle'}
          className={`${showHero ? 'mt-2' : 'mt-1'} flex min-h-[28px] max-w-full items-center gap-1.5 rounded-tp-sm border-0 bg-transparent p-0 text-left text-tp-base tabular-nums text-[#3a4a63] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tp-brand/45`}
          data-testid="trip-summary"
        >
          <span className="min-w-0">
            <SummaryParts summary={summary} />
          </span>
          <span className="sr-only">{metaOpen ? 'Hide dates and vehicle' : 'Show dates and vehicle'}</span>
          <IconChevronDown
            className={`h-4 w-4 flex-none text-tp-faint transition-transform duration-220 ease-tp-out ${metaOpen ? 'rotate-180' : ''}`}
          />
        </button>
      ) : (
        <p className={`m-0 min-w-0 text-tp-base tabular-nums text-[#3a4a63] ${showHero ? 'mt-2.5' : 'mt-1'}`} data-testid="trip-summary">
          <SummaryParts summary={summary} estimateNote={estimateNote} />
        </p>
      )}
      {showMeta && (
        <div
          id={metaId}
          // One segmented control (TripMeta draws the outline and the cells).
          className={`mt-3 ${metaFolds ? 'animate-tp-enter motion-reduce:animate-none' : ''}`}
        >
          {meta}
        </div>
      )}
    </header>
  );
  const noticeSlot = open && notice ? <div className="flex-none">{notice}</div> : null;
  const topSlot = top ? (
    <div className="relative z-20 flex-none border-0 border-t border-solid border-tp-line px-4 pb-3 pt-3">{top}</div>
  ) : null;

  return (
    <section
      aria-label="Trip planner"
      data-testid="trip-planner-card"
      className={`relative flex flex-col bg-white ${
        sheet || flat ? '' : 'rounded-tp-lg border border-solid border-tp-line shadow-tp-card'
      } ${fill ? 'max-h-full min-h-0' : ''} ${className}`}
    >
      {scrollTogether && !headerless && (
        // Phones: ONE header at every snap (peek, half, full): the trip's
        // name and its figures, full width. Solid, never scrolls, a hairline
        // under it once the list shows. At peek the whole header lifts the
        // sheet. While a pin is armed it says what the map is waiting for.
        <div
          className={`relative z-30 flex-none bg-white px-4 pb-3 pt-0.5 ${
            peeking ? '' : 'border-0 border-b border-solid border-tp-line'
          }`}
          data-testid="trip-sheet-header"
          data-sheet-peek-part=""
        >
          {pinPrompt ? (
            pinPrompt
          ) : (
            <div className="flex min-w-0 items-center gap-3">
              <div className="min-w-0 flex-1">
                {peeking || !onTitleChange ? (
                  <span className="block h-7 truncate text-[20px] font-semibold leading-7 tracking-[-0.015em] text-tp-ink">
                    {title || titlePlaceholder}
                  </span>
                ) : (
                  <div className="flex min-w-0 items-center gap-1">
                    <TitleInput title={title} placeholder={titlePlaceholder} onChange={onTitleChange} onCommit={onTitleCommit} compact />
                    {typeof titleMenu === 'function' ? titleMenu(false) : titleMenu}
                  </div>
                )}
                {/* The figures in a dark slate (8.9:1 on white), not a
                    pale grey: they are the trip's facts, read at a glance.
                    The save state leads them as a dot. */}
                <p
                  className="m-0 mt-0.5 flex h-5 min-w-0 items-center gap-1.5 overflow-hidden text-tp-sm leading-5 tabular-nums text-[#3a4a63]"
                  data-testid="trip-summary"
                >
                  {badge ? (
                    <span className="flex flex-none items-center" data-testid="trip-badge">
                      {badgeFor('dot')}
                    </span>
                  ) : null}
                  <span className="min-w-0 truncate" data-testid="trip-compact-figures">
                    {stats && stats.length ? compactFigures : <SummaryParts summary={summary} estimateNote={peeking ? undefined : estimateNote} />}
                  </span>
                </p>
              </div>
              {metaInSheetHeader && !peeking ? (
                <button
                  type="button"
                  onClick={() => setMetaOpen((v) => !v)}
                  aria-expanded={metaOpen}
                  aria-controls={metaId}
                  aria-label={metaOpen ? 'Hide dates and vehicle' : 'Dates and vehicle'}
                  title="Dates and vehicle"
                  className={`relative flex h-11 w-11 min-h-0 min-w-0 flex-none items-center justify-center rounded-full border border-solid p-0 transition-colors duration-150 ease-tp-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tp-brand/45 ${
                    metaOpen
                      ? 'border-tp-brand/40 bg-tp-brand-soft text-tp-brand'
                      : 'border-tp-line-strong bg-white text-tp-ink [@media(hover:hover)]:hover:bg-tp-surface'
                  }`}
                  data-testid="trip-meta-toggle"
                >
                  <IconSliders className="h-5 w-5" />
                </button>
              ) : null}
            </div>
          )}
          {metaInSheetHeader && metaOpen && !peeking && !pinPrompt && (
            <div
              id={metaId}
              className="-mx-4 mt-2.5 flex gap-2 overflow-x-auto px-4 pb-0.5 animate-tp-enter motion-reduce:animate-none [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
              data-testid="trip-sheet-meta"
            >
              {meta}
            </div>
          )}
          {/* The plan's biggest problem, said once, with its fix. */}
          {statsWarning && !peeking && !pinPrompt ? (
            <p className="m-0 mt-1.5 flex min-w-0 items-center gap-1.5 text-tp-sm font-medium text-tp-warn" data-testid="trip-sheet-warning" title={statsWarning.title}>
              <span className="min-w-0 truncate">{statsWarning.text}</span>
              {statsWarning.onFix ? (
                <button
                  type="button"
                  onClick={statsWarning.onFix}
                  className="min-h-0 flex-none rounded-sm border-0 bg-transparent p-0 text-tp-sm font-semibold text-tp-warn underline decoration-tp-warn/40 underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tp-brand/45 [font-family:inherit]"
                >
                  Fix
                </button>
              ) : null}
            </p>
          ) : null}
          {peeking && !pinPrompt && peekPreview ? <div className="mt-1.5">{peekPreview}</div> : null}
          {/* Above peek: the trip's main action, "Add a stop", sits in the
              header under the figures, always in reach (the list scrolls
              under it). */}
          {!peeking && !pinPrompt && top ? (
            <div className="relative z-20 mt-3" data-testid="trip-sheet-add">
              {top}
            </div>
          ) : null}
          {peeking && !pinPrompt && (
            <button
              type="button"
              onClick={onExpand}
              aria-controls={bodyId}
              aria-expanded={false}
              aria-label={`Show the trip: ${title || titlePlaceholder}`}
              className="absolute inset-0 min-h-0 w-full cursor-pointer border-0 bg-transparent p-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-tp-brand/45"
              data-testid="trip-peek-summary"
            />
          )}
        </div>
      )}
      {scrollTogether ? (
        <div className={`relative flex min-h-0 flex-1 flex-col ${peeking ? 'hidden' : ''}`}>
          <div
            ref={bodyRef}
            id={bodyId}
            data-sheet-body
            data-testid="trip-planner-body"
            onScroll={measureEdges}
            // No scroll snapping: the itinerary's sticky day chips sit at the
            // top of this scroller, and a snapped row would hide under them.
            // No scroll anchoring: a row appearing above Day 1 leaves the
            // list at its top.
            className={`min-h-0 flex-1 overflow-y-auto overscroll-contain [overflow-anchor:none] ${peeking ? 'hidden' : ''}`}
            // The header sits above this scroller, so the itinerary's sticky
            // day chips stop at its top edge.
            style={{ ['--tp-sticky-top' as string]: '0px' } as React.CSSProperties}
          >
            {/* The empty planner leads with its own heading: no sheet header,
                its start box scrolls with it. */}
            {noticeSlot}
            {headerless ? topSlot : null}
            {/* Bottom air: the last row clears the footer's top edge. */}
            <div className="px-3 pb-4 pt-1.5">{children}</div>
          </div>
          {/* Rows scrolled under the header fade out under it, never cut by
              a hard edge (a hairline at rest, the fade only once scrolled). */}
          <span
            aria-hidden="true"
            className={`pointer-events-none absolute inset-x-0 top-0 z-10 h-4 bg-[linear-gradient(180deg,#fff_0%,rgba(255,255,255,0)_100%)] transition-opacity duration-150 ease-tp-out ${
              edges.scrolled ? 'opacity-100' : 'opacity-0'
            }`}
            data-testid="trip-scrolled-under"
          />
          {/* More below: a 16px fade into the footer. The sheet's half
              detent lands between rows, so the fade only softens air. */}
          <span
            aria-hidden="true"
            className={`pointer-events-none absolute inset-x-0 bottom-0 z-10 h-4 bg-[linear-gradient(180deg,rgba(255,255,255,0)_0%,#fff_100%)] transition-opacity duration-150 ease-tp-out ${
              edges.more ? 'opacity-100' : 'opacity-0'
            }`}
            data-testid="trip-more-below"
          />
        </div>
      ) : headerless ? null : (
        header
      )}

      {open && (
        <>
          {!scrollTogether && noticeSlot}
          {!scrollTogether && topSlot}
          {!scrollTogether && (
            <div
              id={bodyId}
              data-testid="trip-planner-body"
              // The last row scrolls fully clear of the bar, with room to spare.
              className={`px-3 ${fill ? 'min-h-0 flex-1 overflow-y-auto overscroll-contain pb-10' : 'pb-3'} ${
                top ? 'pt-1' : headerless ? 'pt-3' : 'border-0 border-t border-solid border-tp-line pt-3'
              }`}
            >
              {children}
            </div>
          )}
          {(toast || footer) && (
            <div
              data-testid="trip-footer"
              {...(sheet ? { 'data-sheet-peek-part': '' } : {})}
              // Phones: the sheet's foot, above the home indicator, so Save /
              // Share are always under your thumb at every snap. A solid
              // white surface with a hairline: nothing in the list ever shows
              // through behind the bar.
              className={`relative flex-none border-0 border-t border-solid border-tp-line bg-white px-3 ${
                sheet
                  ? `pt-2 pb-[max(8px,env(safe-area-inset-bottom))] transition-shadow duration-150 ease-tp-out ${
                      scrollTogether && edges.more ? 'shadow-[0_-6px_14px_-8px_rgba(22,35,58,0.22)]' : ''
                    }`
                  : `pt-2.5 shadow-[0_-10px_24px_-14px_rgba(22,35,58,0.32)] ${flat ? 'pb-2.5' : 'rounded-b-tp-lg pb-3'}`
              }`}
            >
              {/* Scroll-under: the list fades out over the last 20px above
                  the bar, so a row is never cut by a hard edge. (The sheet
                  draws its own fade inside the list.) */}
              {!sheet && (
                <span
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-x-0 bottom-full h-5 bg-[linear-gradient(0deg,#fff_0%,rgba(255,255,255,0.85)_40%,rgba(255,255,255,0)_100%)]"
                  data-testid="trip-footer-fade"
                />
              )}
              {floatingAction && scrollTogether && !peeking ? (
                <div className="pointer-events-none absolute inset-x-0 bottom-full z-20 flex justify-center pb-3">
                  <div className="pointer-events-auto">{floatingAction}</div>
                </div>
              ) : null}
              {toast}
              {footer}
            </div>
          )}
        </>
      )}
    </section>
  );
}

/**
 * The trip's hero, the same on every screen: a 56px band, one photo of
 * where you're headed (a brand gradient until one loads, or when there is
 * none). It frames the name and no more, so the figures and the stops sit
 * high. The name sits left, the save chip at the right end.
 */
function TripHero({
  photo,
  onError,
  corner,
  children,
}: {
  photo: { url: string; name: string } | null;
  onError: (url: string) => void;
  corner?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div
      className="relative -mx-1 mb-0.5 flex h-14 items-center gap-2 overflow-hidden rounded-tp-lg bg-[linear-gradient(135deg,#16233a_0%,#1d3f73_55%,#1558b0_100%)] pl-2.5 pr-2 animate-tp-fade motion-reduce:animate-none"
      data-testid="trip-hero"
      data-photos={photo ? 1 : 0}
    >
      {photo && (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          key={photo.url}
          src={photo.url}
          alt=""
          title={/wikipedia|wikimedia/.test(photo.url) ? `${photo.name} · photo via Wikimedia Commons` : photo.name}
          draggable={false}
          onError={() => onError(photo.url)}
          // The band is a strip of the picture's lower middle: water, a
          // road, a skyline. Not the sky.
          className="absolute inset-0 block h-full w-full object-cover object-[50%_62%] animate-tp-fade motion-reduce:animate-none"
        />
      )}
      {/* Two scrims under the words: one rising from the bottom (black 55%
          to 0, as the type sits low), one from the left behind the name.
          White type passes AA on a bright sky as well as a busy skyline. */}
      <span
        aria-hidden="true"
        className="absolute inset-0 bg-[linear-gradient(0deg,rgba(0,0,0,0.55)_0%,rgba(0,0,0,0.38)_55%,rgba(0,0,0,0.18)_100%),linear-gradient(90deg,rgba(0,0,0,0.3)_0%,rgba(0,0,0,0)_70%)]"
      />
      <div className="relative z-[1] min-w-0 flex-1">{children}</div>
      {corner && <div className="relative z-[2] flex-none">{corner}</div>}
    </div>
  );
}

/**
 * The editable trip name. It looks like a title; a 32px pencil pill right
 * after the words says it can be changed (always shown: touch has no
 * hover). The field sizes to its text, so the pill sits beside the name.
 */
function TitleInput({
  title,
  placeholder,
  onChange,
  onCommit,
  onImage = false,
  compact = false,
}: {
  title: string;
  placeholder: string;
  onChange: (value: string) => void;
  onCommit?: () => void;
  onImage?: boolean;
  /** Phones' sheet header: 20px in a 28px line, over the figures line. */
  compact?: boolean;
}) {
  const ref = React.useRef<HTMLInputElement>(null);
  // Inline type (the storefront theme resets input fonts and margins with
  // selectors stronger than utilities). The mirror uses the same, so the
  // field is exactly as wide as its words.
  const type: React.CSSProperties = {
    fontSize: 20,
    lineHeight: compact ? '28px' : '26px',
    fontWeight: 600,
    letterSpacing: '-0.015em',
    // Inputs don't inherit the page font by default: without this the field's
    // words come out narrower than the mirror sizing it, and the pencil
    // drifts away from the name.
    fontFamily: 'inherit',
  };
  return (
    <div className="group/title flex min-w-0 flex-1 items-center gap-2" style={{ marginLeft: -6 }}>
      <span className="inline-grid min-w-0 max-w-full [grid-template-columns:minmax(0,max-content)]">
        <span
          aria-hidden="true"
          className="invisible col-start-1 row-start-1 overflow-hidden whitespace-pre border border-solid border-transparent px-1.5"
          style={type}
        >
          {title || placeholder}
        </span>
        <input
          ref={ref}
          id="trip-name-input"
          type="text"
          value={title}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              onCommit?.();
              (e.target as HTMLInputElement).blur();
            }
          }}
          placeholder={placeholder}
          aria-label="Trip name"
          title="Rename this trip"
          maxLength={80}
          spellCheck={false}
          size={1}
          className={`col-start-1 row-start-1 box-border block w-full min-w-0 truncate rounded-tp-sm border border-solid border-transparent px-1.5 transition-[border-color,background-color] duration-150 ease-tp-out focus:outline-none ${
            onImage
              ? 'bg-transparent text-white placeholder:text-white [text-shadow:0_1px_3px_rgba(0,0,0,0.45)] hover:border-white/40 focus:border-white/70 focus:bg-black/40 focus:placeholder:text-white/60'
              : 'bg-transparent text-tp-ink placeholder:text-tp-ink hover:border-tp-line hover:bg-tp-surface focus:border-tp-brand focus:bg-white focus:ring-2 focus:ring-tp-brand/20 focus:placeholder:text-tp-faint'
          }`}
          style={{ ...type, height: compact ? 28 : 34, margin: 0 }}
        />
      </span>
      {/* The affordance: a 20px pencil on a 32px disc (44px with its pad).
          Not a tab stop: the field itself is, and a tap anywhere on the name
          renames too. */}
      <button
        type="button"
        tabIndex={-1}
        aria-hidden="true"
        title="Rename this trip"
        onClick={() => {
          ref.current?.focus();
          ref.current?.select();
        }}
        // 28px to the eye, a 44px target to the thumb (the ::after pad);
        // min-w-0 beats the touch rule that widened it and pushed the
        // pencil off the end of the name.
        // On the photo it stays out of the picture: with a mouse it shows on
        // hover (or while the name has focus); on touch, a plain white
        // glyph with no disc, since there is no hover to wait for.
        className={`relative -ml-1 flex h-8 w-8 min-h-0 min-w-0 flex-none items-center after:absolute after:-inset-1.5 after:content-['']  justify-center rounded-full border-0 transition-[opacity,background-color,color] duration-150 ease-tp-out group-focus-within/title:pointer-events-none group-focus-within/title:opacity-0 ${
          onImage
            ? 'bg-transparent text-white/90 [filter:drop-shadow(0_1px_2px_rgba(0,0,0,0.5))] hover:bg-black/30 hover:text-white [@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-hover/title:opacity-100'
            : 'bg-transparent text-tp-muted hover:bg-tp-surface hover:text-tp-ink'
        }`}
        data-testid="trip-name-edit"
      >
        <IconPencil className={onImage ? 'h-[18px] w-[18px]' : 'h-5 w-5'} />
      </button>
    </div>
  );
}

/**
 * The summary, one unbroken chunk per figure: "2 nights" never splits across
 * lines. Every gap is the same 6px — figure · figure — and the dot leads the
 * figure after it, so a wrapped line never ends on a stray separator.
 */
const NBSP = String.fromCharCode(160);

function SummaryParts({ summary, estimateNote }: { summary: string; estimateNote?: string }) {
  const parts = summary.split(' · ');
  // A sentence (empty / one-stop pitch) wraps like any sentence.
  if (parts.length < 2) return <>{summary}</>;
  return (
    <span className="inline-flex flex-wrap items-center gap-x-1.5">
      {parts.map((part, i) => {
        const approx = part.startsWith('≈ ');
        return (
          <span key={i} className="inline-flex items-center gap-x-1.5 whitespace-nowrap">
            {i > 0 && (
              <span className="text-tp-faint" aria-hidden="true">
                ·
              </span>
            )}
            <span className="inline-flex items-center gap-x-1">
              {part.replace(/ /g, NBSP)}
              {approx && estimateNote && (
                <span
                  role="img"
                  tabIndex={0}
                  aria-label={estimateNote}
                  title={estimateNote}
                  className="inline-flex h-3.5 w-3.5 cursor-help items-center justify-center rounded-full text-tp-faint hover:text-tp-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tp-brand/45"
                >
                  <IconInfo className="h-3.5 w-3.5" />
                </span>
              )}
            </span>
          </span>
        );
      })}
    </span>
  );
}

/** "4 stops · ≈ 1,285 km · 18h 21m · 2 nights" — the header summary under the title. */
export function tripSummaryLine({
  stopCount,
  distanceKm,
  driveMinutes,
  estimate,
  nights,
}: {
  stopCount: number;
  distanceKm?: number;
  driveMinutes?: number;
  estimate: boolean;
  nights: number;
}): string {
  if (stopCount === 0) return 'Pick a start, then where you’re headed';
  if (stopCount === 1) return 'Add where you’re headed next';
  const approx = estimate ? '≈ ' : '';
  const kmLabel = formatKm(distanceKm);
  const driveLabel = formatDrive(driveMinutes);
  // One "≈" covers the figures that follow it.
  return [
    `${stopCount} stops`,
    kmLabel ? `${approx}${kmLabel}` : null,
    driveLabel || null,
    nights > 0 ? `${nights} night${nights === 1 ? '' : 's'}` : null,
  ]
    .filter(Boolean)
    .join(' · ');
}
